import { Box, FormatNumber, HStack, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import type { ReactNode } from 'react';

interface MentionMetricPillProps {
  label: string;
  value: number | string | ReactNode | null | undefined;
  /** When set with a numeric value, the value formats as currency (e.g. "€247.50"). */
  currency?: string;
  /** Optional explainer: renders the label with a dotted underline + tooltip on hover. */
  infoTip?: string;
  /** Optional leading glyph. The organic counters carry one; the paid KPIs do not. */
  icon?: ReactNode;
}

/** Numbers format as currency or compact notation; anything else renders verbatim. */
function formatMetricValue(value: MentionMetricPillProps['value'], currency?: string): ReactNode {
  if (typeof value !== 'number') {
    return value ?? '--';
  }
  if (currency) {
    return <FormatNumber value={value} style="currency" currency={currency} />;
  }
  return <FormatNumber value={value} notation="compact" compactDisplay="short" />;
}

/**
 * The single metric pill used by both stat blocks of the mention pane: the
 * creator's organic counters and the boost's paid KPIs. One horizontal row —
 * optional icon, label, then the value pushed right — laid out two per line by
 * the calling grid, so the two blocks read as one system.
 */
export function MentionMetricPill({
  label,
  value,
  currency,
  infoTip,
  icon,
}: MentionMetricPillProps) {
  const content = formatMetricValue(value, currency);

  const labelNode = infoTip ? (
    <Tooltip content={infoTip} showArrow portalled positioning={{ placement: 'top' }}>
      <Text
        fontSize="sm"
        color="gray.600"
        truncate
        cursor="help"
        css={{
          textDecorationLine: 'underline',
          textDecorationStyle: 'dotted',
          textDecorationColor: 'currentColor',
          textUnderlineOffset: '3px',
        }}
      >
        {label}
      </Text>
    </Tooltip>
  ) : (
    <Text fontSize="sm" color="gray.600" truncate>
      {label}
    </Text>
  );

  return (
    <HStack minW={0} gap={2} bg="gray.50" rounded="md" px={3} py={2.5}>
      {icon && (
        <Box flexShrink={0} aria-hidden="true" lineHeight={0} color="gray.500">
          {icon}
        </Box>
      )}
      {labelNode}
      <Text
        ml="auto"
        fontSize="sm"
        fontWeight="semibold"
        color="gray.900"
        flexShrink={0}
        css={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {content}
      </Text>
    </HStack>
  );
}
